import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {Promise<object | null> | undefined} */
  #productDataPromise;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);

    if (this.dataset.templateProductMatch === 'true' && this.dataset.productUrl) {
      this.#preloadProductData();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? '',
    }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    const requestUrl = this.buildRequestUrl(selectedOption);

    if (isOnProductPage && !loadsNewProduct && !isOnFeaturedProductSection) {
      this.updateProductFromCachedVariantData(selectedOption).then((variantId) => {
        if (variantId) {
          this.updateProductPageUrl(String(variantId));
        } else {
          this.fetchUpdatedSection(requestUrl, morphElementSelector);
          this.updateProductPageUrl(selectedOption.dataset.variantId || null);
        }
      });
    } else {
      this.fetchUpdatedSection(requestUrl, morphElementSelector);
      this.updateProductPageUrl(selectedOption.dataset.variantId || null, loadsNewProduct ? newUrl : undefined);
    }
  }

  /**
   * Loads compact Shopify product JSON once so variant clicks do not need to fetch the full product page HTML.
   * @returns {Promise<object | null>}
   */
  #preloadProductData() {
    if (this.#productDataPromise) return this.#productDataPromise;

    const productUrl = this.dataset.productUrl?.split('?')[0];
    if (!productUrl) return Promise.resolve(null);

    this.#productDataPromise = fetch(`${productUrl}.js`, {
      headers: { Accept: 'application/json' },
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((product) => {
        this.#preloadVariantImages(product);
        return product;
      })
      .catch((error) => {
        console.warn('Variant product data preload failed', error);
        return null;
      });

    return this.#productDataPromise;
  }

  /**
   * Preloads variant images after initial render to make later variant image switches feel immediate.
   * @param {any} product
   */
  #preloadVariantImages(product) {
    if (!product?.variants?.length) return;

    const seen = new Set();
    for (const variant of product.variants) {
      const src = variant?.featured_image?.src;
      if (!src || seen.has(src)) continue;

      seen.add(src);
      const image = new Image();
      image.src = this.#withImageWidth(src, 1200);
    }
  }

  /**
   * Updates the product UI from compact variant data instead of fetching and morphing product-page HTML.
   * Falls back to the original server-rendered path when the local data is incomplete.
   * @param {HTMLElement} selectedOption
   * @returns {Promise<string | number | false>}
   */
  async updateProductFromCachedVariantData(selectedOption) {
    const product = await this.#preloadProductData();
    const variant = this.#findSelectedVariant(product);

    if (!variant) return false;

    this.#selectVariantMedia(variant);

    if (!this.selectedOptionId && !selectedOption.dataset.optionValueId) return false;

    this.dispatchEvent(
      new VariantUpdateEvent(this.#buildVariantResource(variant), this.selectedOptionId ?? selectedOption.dataset.optionValueId ?? '', {
        html: this.#buildSyntheticVariantHtml(variant),
        productId: this.dataset.productId ?? '',
      })
    );

    return variant.id ?? false;
  }

  /**
   * @param {string | null} variantId
   * @param {string | undefined} [newPath]
   */
  updateProductPageUrl(variantId, newPath) {
    const url = new URL(window.location.href);

    if (variantId) {
      url.searchParams.set('variant', variantId);
    } else {
      url.searchParams.delete('variant');
    }

    if (newPath) {
      url.pathname = newPath;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @param {any} product
   * @returns {any | undefined}
   */
  #findSelectedVariant(product) {
    const selectedValues = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      (option) => /** @type {HTMLInputElement | HTMLOptionElement} */ (option).value
    );

    if (!product?.variants?.length || !selectedValues.length) return undefined;

    return product.variants.find((variant) =>
      selectedValues.every((value, index) => {
        const optionValue = variant.options?.[index] ?? variant[`option${index + 1}`];
        return optionValue === value;
      })
    );
  }

  /**
   * @param {any} variant
   * @returns {any}
   */
  #buildVariantResource(variant) {
    return {
      ...variant,
      featured_media: variant.featured_image
        ? {
            preview_image: {
              src: variant.featured_image.src,
            },
          }
        : undefined,
    };
  }

  /**
   * @param {any} variant
   * @returns {Document}
   */
  #buildSyntheticVariantHtml(variant) {
    const html = document.implementation.createHTMLDocument('variant-update');

    for (const productPrice of document.querySelectorAll('product-price')) {
      const clone = productPrice.cloneNode(true);
      if (clone instanceof HTMLElement) {
        this.#updatePriceClone(clone, variant);
        html.body.appendChild(clone);
      }
    }

    for (const productForm of document.querySelectorAll('product-form-component')) {
      const clone = productForm.cloneNode(true);
      if (clone instanceof HTMLElement) {
        this.#updateFormClone(clone, variant);
        html.body.appendChild(clone);
      }
    }

    return html;
  }

  /**
   * @param {HTMLElement} productPrice
   * @param {any} variant
   */
  #updatePriceClone(productPrice, variant) {
    const price = this.#formatMoney(variant.price);
    const compareAtPrice = Number(variant.compare_at_price || 0);
    const hasCompareAtPrice = compareAtPrice > Number(variant.price || 0);

    const regularWrapper = productPrice.querySelector('.price__regular');
    const saleWrapper = productPrice.querySelector('.price__sale');
    const regularPrice = productPrice.querySelector('.price__regular .price');
    const salePrice = productPrice.querySelector('.price-item--sale.price');
    const compareAtPriceElement = productPrice.querySelector('.price-item--regular.compare-at-price');

    regularPrice && (regularPrice.textContent = price);
    salePrice && (salePrice.textContent = price);

    if (hasCompareAtPrice) {
      regularWrapper?.classList.add('price__hidden');
      saleWrapper?.classList.remove('price__hidden');
      compareAtPriceElement && (compareAtPriceElement.textContent = this.#formatMoney(compareAtPrice));
    } else {
      regularWrapper?.classList.remove('price__hidden');
      saleWrapper?.classList.add('price__hidden');
      compareAtPriceElement && (compareAtPriceElement.textContent = '');
    }
  }

  /**
   * @param {HTMLElement} productForm
   * @param {any} variant
   */
  #updateFormClone(productForm, variant) {
    productForm.querySelectorAll('input[name="id"]').forEach((input) => {
      if (input instanceof HTMLInputElement) input.value = String(variant.id ?? '');
    });

    productForm.querySelectorAll('[ref="addToCartButton"]').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;

      if (variant.available === false) {
        button.disabled = true;
      } else {
        button.disabled = false;
        button.removeAttribute('disabled');
      }
    });
  }

  /**
   * @param {number} cents
   * @returns {string}
   */
  #formatMoney(cents) {
    const samplePrice = document.querySelector('product-price .price')?.textContent?.trim() || '$0.00';
    const symbol = samplePrice.match(/^[^\d-]+/)?.[0] || '$';
    return `${symbol}${(Number(cents || 0) / 100).toFixed(2)}`;
  }

  /**
   * Selects the existing media-gallery slide for the variant image when the image is already in the gallery.
   * @param {any} variant
   */
  #selectVariantMedia(variant) {
    const imageSrc = variant?.featured_image?.src;
    if (!imageSrc) return;

    const targetImageKey = this.#imageKey(imageSrc);
    if (!targetImageKey) return;

    const gallery = document.querySelector('media-gallery');
    if (!gallery) return;

    const matchedImage = Array.from(gallery.querySelectorAll('img')).find((image) => {
      const src = image.currentSrc || image.getAttribute('src') || '';
      return this.#imageKey(src) === targetImageKey;
    });

    const slide = matchedImage?.closest('slideshow-slide');
    if (!slide) {
      this.#replaceCurrentGalleryImage(gallery, imageSrc);
      return;
    }

    const slides = Array.from(slide.parentElement?.querySelectorAll('slideshow-slide') ?? []);
    const index = slides.indexOf(slide);
    if (index < 0) return;

    const slideshow = /** @type {any} */ (gallery).slideshow;
    if (slideshow?.select) {
      slideshow.select(index, undefined, { animate: false });
    } else {
      slide.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }

  /**
   * Replaces the currently visible gallery image when Shopify has not rendered the variant image as a slide yet.
   * @param {Element} gallery
   * @param {string} imageSrc
   */
  #replaceCurrentGalleryImage(gallery, imageSrc) {
    const activeSlide =
      gallery.querySelector('slideshow-slide[aria-hidden="false"]') ?? gallery.querySelector('slideshow-slide');
    const image = activeSlide?.querySelector('img.product-media__image, img');
    if (!(image instanceof HTMLImageElement)) return;

    image.src = this.#withImageWidth(imageSrc, 3840);
    image.srcset = this.#buildImageSrcset(imageSrc);
    image.setAttribute('data_max_resolution', this.#withImageWidth(imageSrc, 3840));
    image.removeAttribute('loading');
    image.setAttribute('fetchpriority', 'high');
  }

  /**
   * @param {string} src
   * @returns {string}
   */
  #buildImageSrcset(src) {
    return [240, 352, 832, 1200, 1600, 1920, 2560, 3840]
      .map((width) => `${this.#withImageWidth(src, width)} ${width}w`)
      .join(', ');
  }

  /**
   * @param {string} src
   * @returns {string}
   */
  #imageKey(src) {
    try {
      return new URL(src, window.location.href).pathname;
    } catch (_error) {
      return src.split('?')[0].replace(/^\/\//, '');
    }
  }

  /**
   * @param {string} src
   * @param {number} width
   * @returns {string}
   */
  #withImageWidth(src, width) {
    const separator = src.includes('?') ? '&' : '?';
    return `${src}${separator}width=${width}`;
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        // Dispatch for all paths so product-form-component can reset #variantChangeInProgress
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            })
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
